import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarDaysIcon, CheckCircleIcon, ClockIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/Button';
import { FormTextarea } from '@/components/FormTextarea';
import { fetchDayOffClientView, respondDayOffClient, type DayOffClientView } from '../../services/dpRequestCallables';
import { formatIsoDate } from '../../utils/formatDayOffDates';
import { CLIENT_RESULT_COPY } from './dayOffClientApproval.definitions';

type Phase = 'loading' | 'error' | 'ready' | 'done';

export function DayOffClientApprovalPage() {
    const { token } = useParams<{ token: string }>();
    const [phase, setPhase] = useState<Phase>('loading');
    const [view, setView] = useState<DayOffClientView | null>(null);
    const [outcome, setOutcome] = useState<'approved' | 'rejected' | null>(null);
    const [rejecting, setRejecting] = useState(false);
    const [note, setNote] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!token) {
            setView({ state: 'not_found' });
            setPhase('done');
            return;
        }
        fetchDayOffClientView(token)
            .then(result => {
                setView(result);
                setPhase(result.state === 'pending' ? 'ready' : 'done');
            })
            .catch(() => setPhase('error'));
    }, [token]);

    async function respond(approve: boolean) {
        if (!token) return;
        if (!approve && note.trim() === '') {
            setError('Conte o motivo da recusa para o colaborador e o gestor.');
            return;
        }
        setError(null);
        setSending(true);
        try {
            const result = await respondDayOffClient({ token, approve, note: note.trim() || undefined });
            if (result === 'success') {
                setOutcome(approve ? 'approved' : 'rejected');
                setPhase('done');
            } else {
                const refreshed = await fetchDayOffClientView(token);
                setView(refreshed);
                setPhase('done');
            }
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'Não foi possível registrar sua resposta.');
        } finally {
            setSending(false);
        }
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-50 to-gray-100 p-4">
            <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg sm:p-8">
                {phase === 'loading' && <p className="text-center text-sm text-gray-600" role="status">Carregando…</p>}
                {phase === 'error' && (
                    <ResultBlock icon={<XCircleIcon className="mx-auto h-14 w-14 text-rose-500" aria-hidden="true" />} title={CLIENT_RESULT_COPY.error.title} text={CLIENT_RESULT_COPY.error.text} />
                )}
                {phase === 'ready' && view && (
                    <div className="space-y-5">
                        <div className="text-center">
                            <CalendarDaysIcon className="mx-auto h-12 w-12 text-primary-600" aria-hidden="true" />
                            <h1 className="mt-2 font-montserrat text-xl font-semibold text-gray-900">Aprovação de day off</h1>
                        </div>
                        <p className="text-sm text-gray-700">
                            <strong>{view.collaboratorName}</strong> pediu o day off de aniversário em{' '}
                            <strong>{view.date ? formatIsoDate(view.date) : ''}</strong>. Você autoriza a folga nesse dia?
                        </p>

                        {rejecting && (
                            <FormTextarea
                                label="Motivo da recusa"
                                name="client-note"
                                value={note}
                                onChange={event => setNote(event.target.value)}
                                maxLength={500}
                                showCharCount
                                rows={3}
                                required
                            />
                        )}
                        {error && <p className="text-sm text-rose-700" role="alert">{error}</p>}

                        {rejecting ? (
                            <div className="flex gap-3">
                                <Button variant="ghost" className="flex-1" onClick={() => { setRejecting(false); setError(null); }} disabled={sending}>Voltar</Button>
                                <Button variant="danger" className="flex-1" onClick={() => void respond(false)} loading={sending}>Confirmar recusa</Button>
                            </div>
                        ) : (
                            <div className="flex gap-3">
                                <Button variant="outline" className="flex-1" onClick={() => setRejecting(true)} disabled={sending}>Recusar</Button>
                                <Button className="flex-1" onClick={() => void respond(true)} loading={sending}>Aprovar</Button>
                            </div>
                        )}
                    </div>
                )}
                {phase === 'done' && (
                    <DoneBlock state={outcome ?? view?.state ?? 'not_found'} />
                )}
            </div>
        </div>
    );
}

function DoneBlock({ state }: { state: string }) {
    const copy = CLIENT_RESULT_COPY[state as keyof typeof CLIENT_RESULT_COPY] ?? CLIENT_RESULT_COPY.not_found;
    const Icon = state === 'approved' || state === 'already_approved' ? CheckCircleIcon : state === 'expired' || state === 'closed' ? ClockIcon : XCircleIcon;
    const color = state === 'approved' || state === 'already_approved' ? 'text-green-600' : 'text-gray-400';
    return <ResultBlock icon={<Icon className={`mx-auto h-14 w-14 ${color}`} aria-hidden="true" />} title={copy.title} text={copy.text} />;
}

function ResultBlock({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
    return (
        <div className="space-y-3 text-center">
            {icon}
            <h1 className="font-montserrat text-xl font-semibold text-gray-900">{title}</h1>
            <p className="text-sm text-gray-600">{text}</p>
        </div>
    );
}

export default DayOffClientApprovalPage;
